
// packages/components/src/platform_features/agent-builder/components/steps/AgentAbilitiesStep.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '@dopomogai/ui';
import { ToolList } from '../lists/ToolList';
import { FeatureTeaser } from '../teasers/FeatureTeaser';
import type { ToolResponse, PlatformToolData, CustomToolData } from '@dopomogai/types';
import { Link } from 'react-router-dom';

interface AgentAbilitiesStepProps {
  availableTools: ToolResponse[];
  selectedToolSlugs: string[];
  onToolToggle: (toolSlug: string) => void;
}

export const AgentAbilitiesStep: React.FC<AgentAbilitiesStepProps> = ({
  availableTools,
  selectedToolSlugs,
  onToolToggle,
}) => {
  const { t } = useTranslation('agentBuilder');

  const toolsForUI = useMemo(() => {
    return availableTools.map((tool): PlatformToolData | CustomToolData => {
        const isEnabled = selectedToolSlugs.includes(tool.slug);
        if (tool.is_platform_tool) {
            return {
                id: tool.id,
                isEnabled,
                isMutable: true,
                nameKey: tool.name, 
                descriptionKey: tool.description || '',
                icon: tool.slug,
            };
        } else {
            return {
                id: tool.id,
                name: tool.name,
                description: tool.description ?? undefined,
                isEnabled,
                isMutable: true,
                icon: 'construction',
            };
        }
    });
  }, [availableTools, selectedToolSlugs]);
  
  const platformTools = toolsForUI.filter(t => 'nameKey' in t) as PlatformToolData[];
  const customTools = toolsForUI.filter(t => !('nameKey' in t)) as CustomToolData[];

  const handleToggleById = (toolId: string) => {
    const tool = availableTools.find(t => t.id === toolId);
    if (tool) onToolToggle(tool.slug);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('step3.pageTitle')}</h2>
        <p className="text-slate-600">{t('step3.pageDescription')}</p>
      </div>

      <Card title={t('step3.platformSkills.subheading')} headerBgColor="bg-orange-200">
        <div className="pt-2">
          <ToolList
            tools={platformTools}
            toolType="platform"
            onToggleTool={handleToggleById}
          />
        </div>
      </Card>
      
      <Card title={t('step3.customSkills.subheading')} headerBgColor="bg-orange-200">
        <div className='pt-2 space-y-3'>
          <p className="text-sm text-slate-600">
            You can add your own tools later from the Tools Hub, then enable them here in the Command Center.
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/app/tools">Open Tools Hub</Link>
          </Button>
          {customTools.length > 0 ? (
            <ToolList
              tools={customTools}
              toolType="custom"
              onToggleTool={handleToggleById}
              onEditTool={() => alert('Editing tools is coming soon!')}
              onDeleteTool={() => alert('Deleting tools is coming soon!')}
            />
          ) : (
            <FeatureTeaser 
              titleKey="teasers.customSkills.title"
              descriptionKey="teasers.customSkills.description"
            />
          )}
        </div>
      </Card>
      
      <Card title={t('step3.externalMcp.subheading')} headerBgColor="bg-orange-200">
        <FeatureTeaser 
          titleKey="teasers.mcp.title"
          descriptionKey="teasers.mcp.description"
        />
      </Card>
    </div>
  );
};